import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BookOpen,
  LayoutDashboard,
  Users,
  Stethoscope,
  Heart,
  Calendar,
  Archive,
  Settings,
  Database,
  HelpCircle,
  MessageSquare,
  X,
  ChevronRight,
  Search,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Shield,
} from 'lucide-react'

const AppHelpGuide = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const location = useLocation()

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      path: '/dashboard',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Welcome to NaturAssist! This guide will help you navigate and use all features of the application.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Quick Start:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Complete onboarding if this is your first time</li>
              <li>Explore the Dashboard for an overview</li>
              <li>Add your first client</li>
              <li>Create an examination</li>
              <li>Set up a care plan</li>
            </ol>
          </div>
          <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">Your Subscription:</h4>
            <p className="text-sm text-primary-800 dark:text-primary-200">
              Check Organization Settings to see your current tier and available features.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            The Dashboard provides a quick overview of your practice with key metrics and recent activity.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">What You'll See:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600" />
                <span>Total clients count</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600" />
                <span>Active care plans</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600" />
                <span>Upcoming sessions</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 text-green-600" />
                <span>Recent activity feed</span>
              </li>
            </ul>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Quick Actions:</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Use quick action buttons to add clients, create examinations, or start new care plans directly from the dashboard.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'clients',
      title: 'Client Management',
      icon: Users,
      path: '/clients',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Manage all your clients in one place. Add, view, edit, and organize client information.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Key Features:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Add new clients with comprehensive information</li>
              <li>• Search and filter clients</li>
              <li>• View detailed client profiles</li>
              <li>• Organize with tags</li>
              <li>• Track medical history and examinations</li>
              <li>• Manage documents and communications</li>
            </ul>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Tip:</h4>
            <p className="text-sm text-green-800 dark:text-green-200">
              Use tags to organize clients by condition, treatment type, or any custom category. This makes filtering and finding clients much easier.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'examination',
      title: 'Examinations',
      icon: Stethoscope,
      path: '/examination',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Create comprehensive examinations with detailed medical history, vital signs, and custom questionnaires.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Examination Sections:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Personal Information</li>
              <li>Medical History</li>
              <li>Lifestyle & Wellness</li>
              <li>Health Goals</li>
              <li>Vital Signs</li>
              <li>Questionnaire & Charting</li>
              <li>Client Consent</li>
            </ol>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Important:</h4>
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Always obtain client consent before saving examination data. This is required for GDPR compliance.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'care-plans',
      title: 'Care Plans',
      icon: Heart,
      path: '/care-plans',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Create and manage individualized care plans with goals, interventions, and outcome tracking.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Creating Care Plans:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Select a client</li>
              <li>Choose a template (Professional+) or create custom</li>
              <li>Set health goals</li>
              <li>Define interventions</li>
              <li>Set timeline and outcomes</li>
              <li>Save and track progress</li>
            </ol>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Best Practice:</h4>
            <p className="text-sm text-purple-800 dark:text-purple-200">
              Review and update care plans regularly based on client progress. Set specific, measurable goals for better tracking.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'therapies',
      title: 'Therapies',
      icon: Calendar,
      path: '/therapies',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Manage therapy types and protocols. Create therapies that can be scheduled as sessions.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Therapy Management:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Create therapy types (e.g., Acupuncture, Hydrotherapy)</li>
              <li>• Set duration and frequency</li>
              <li>• Link to clients</li>
              <li>• Schedule sessions</li>
              <li>• Use calendar view (Professional+)</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'sessions',
      title: 'Sessions',
      icon: Archive,
      path: '/sessions',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Track therapy sessions with notes, outcomes, and scheduling.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Session Features:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Schedule therapy sessions</li>
              <li>• Add session notes</li>
              <li>• Record outcomes</li>
              <li>• Track attendance</li>
              <li>• Use templates (Professional+)</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'organization',
      title: 'Organization Settings',
      icon: Settings,
      path: '/organization',
      adminOnly: true,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Manage your organization, team members, and subscription. (Admin only)
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Settings Available:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• View/Edit organization details</li>
              <li>• Manage team members and roles</li>
              <li>• View subscription and billing</li>
              <li>• Monitor storage usage</li>
              <li>• Upgrade subscription</li>
            </ul>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 mb-2" />
            <p className="text-sm text-red-800 dark:text-red-200">
              Only organization admins can access these settings. Contact your admin if you need changes.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'backup-restore',
      title: 'Backup & Restore',
      icon: Database,
      path: '/backup-restore',
      adminOnly: true,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Create backups of your data and restore when needed. (Admin only)
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Backup Process:</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Select data to backup</li>
              <li>Click "Create Backup"</li>
              <li>Download backup file</li>
              <li>Store securely</li>
            </ol>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5 text-orange-600 mb-2" />
            <p className="text-sm text-orange-800 dark:text-orange-200">
              <strong>Warning:</strong> Restoring will overwrite existing data. Always create a backup before restoring.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'support',
      title: 'Support',
      icon: HelpCircle,
      path: '/support',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Get help, submit tickets, and access support resources.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Support Options:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Create support tickets</li>
              <li>• View ticket status</li>
              <li>• Access FAQ</li>
              <li>• Contact support team</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'forum',
      title: 'Forum',
      icon: MessageSquare,
      path: '/forum',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Connect with other practitioners, ask questions, and share knowledge.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Forum Features:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Browse discussions by category</li>
              <li>• Search topics</li>
              <li>• Create new topics</li>
              <li>• Reply and help others</li>
              <li>• React to helpful posts</li>
            </ul>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Available to all subscribers. Some categories require Professional or Enterprise tier.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'permissions',
      title: 'Permissions & Access',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Understand user roles and feature access based on subscription tiers.
          </p>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">User Roles:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>Admin:</strong> Full access, manage organization</li>
              <li><strong>Practitioner:</strong> Create and edit clients, examinations, care plans</li>
              <li><strong>Viewer:</strong> Read-only access</li>
            </ul>
          </div>
          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
            <h4 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-2">Feature Access:</h4>
            <p className="text-sm text-indigo-800 dark:text-indigo-200">
              Some features require Professional or Enterprise subscription. Check Organization Settings to see your tier and available features.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'tips',
      title: 'Tips & Best Practices',
      icon: Lightbulb,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">General Tips:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Use tags to organize clients</li>
              <li>• Create regular backups</li>
              <li>• Keep client information updated</li>
              <li>• Use templates to save time (Professional+)</li>
              <li>• Monitor storage usage</li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">Data Management:</h4>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• Complete all examination sections thoroughly</li>
              <li>• Set clear, measurable care plan goals</li>
              <li>• Update care plans based on progress</li>
              <li>• Document all client interactions</li>
            </ul>
          </div>
        </div>
      ),
    },
  ]

  const filteredSections = sections.filter((section) => {
    if (searchQuery) {
      return (
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.content.props.children.some((child) =>
          typeof child === 'string' ? child.toLowerCase().includes(searchQuery.toLowerCase()) : false
        )
      )
    }
    return true
  })

  const currentSection = sections.find((s) => location.pathname.startsWith(s.path))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl my-8 max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">NaturAssist User Guide</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Complete guide to using NaturAssist</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close guide"
              >
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guide..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 dark:border-gray-700 rounded-lg focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredSections.map((section) => {
              const Icon = section.icon
              const isActive = activeSection === section.id
              return (
                <motion.button
                  key={section.id}
                  onClick={() => setActiveSection(isActive ? null : section.id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                  } ${currentSection?.id === section.id ? 'ring-2 ring-primary-500' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {section.title}
                        </h3>
                        {section.path && (
                          <Link
                            to={section.path}
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                          >
                            <ChevronRight size={16} />
                          </Link>
                        )}
                      </div>
                      {section.adminOnly && (
                        <span className="inline-block px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs font-medium mb-2">
                          Admin Only
                        </span>
                      )}
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-3 text-sm text-gray-700 dark:text-gray-300"
                        >
                          {section.content}
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.button>
              )
            })}
          </div>

          {filteredSections.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No results found</p>
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Link
                to="/support"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <HelpCircle size={20} className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">Contact Support</span>
              </Link>
              <Link
                to="/forum"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <MessageSquare size={20} className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">Community Forum</span>
              </Link>
              <Link
                to="/organization"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Settings size={20} className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">Organization Settings</span>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default AppHelpGuide
